<template>
  <div class="agenda-mensal-page" data-testid="agenda-mensal-page">
    <div class="agenda-header">
      <div class="agenda-header-left">
        <i class="pi pi-calendar agenda-header-icon"></i>
        <h1>{{ t('Agenda de produção') }}</h1>
      </div>
      <div class="agenda-controls">
        <button class="agenda-nav-btn" data-testid="agenda-prev-month" @click="prevMonth"><i class="pi pi-chevron-left"></i></button>
        <span class="agenda-month-label">{{ monthLabel }}</span>
        <button class="agenda-nav-btn" data-testid="agenda-next-month" @click="nextMonth"><i class="pi pi-chevron-right"></i></button>
        <i class="pi pi-refresh agenda-refresh-icon" @click="fetchData"></i>
        <span class="agenda-updated">{{ t('Atualizado') }}: {{ lastUpdated }}</span>
        <span class="agenda-clock">{{ currentTime }}</span>
      </div>
    </div>

    <div class="agenda-legend">
      <span class="legend-item"><span class="legend-swatch legend-overdue"></span> {{ t('Atrasado') }}</span>
      <span class="legend-item"><span class="legend-swatch legend-today"></span> {{ t('Hoje') }}</span>
      <span class="legend-item"><span class="legend-swatch legend-tomorrow"></span> {{ t('Amanhã') }}</span>
      <span class="legend-item"><span class="legend-swatch legend-upcoming"></span> {{ t('Próximos') }}</span>
      <span class="legend-item"><span class="legend-swatch legend-rescheduled"></span> {{ t('Reagendado') }}</span>
    </div>

    <div class="agenda-dow-header" data-testid="agenda-dow-header">
      <div v-for="dow in dayOfWeekLabels" :key="dow" class="agenda-dow-cell">{{ dow }}</div>
    </div>

    <div class="agenda-grid" data-testid="agenda-calendar-grid">
      <div v-for="cell in monthCells" :key="cell.key" class="agenda-day-cell" :class="{ 'is-today': cell.isToday, 'is-other-month': !cell.isCurrentMonth, 'has-items': cell.items.length > 0 }" :data-testid="`agenda-day-${cell.dateStr}`" @dragover="cell.isCurrentMonth ? onDragOver($event) : null" @dragleave="onDragLeave($event)" @drop="cell.isCurrentMonth ? onDrop($event, cell) : null">
        <span class="day-number">{{ cell.day }}</span>
        <div class="day-cards" v-if="cell.items.length > 0">
          <div v-for="item in cell.items.slice(0, MAX_CARDS_PER_DAY)" :key="item.so_work_order_step_id ?? `item-${item.so_order_item_id}`" class="day-card" :class="urgencyDaysClass(item)" :data-testid="`agenda-card-${item.so_order_item_id}`" draggable="true" @dragstart="onDragStart($event, item)" @dragend="onDragEnd" @click.stop.prevent="openItemDetail(item)">
            <img v-if="item.item_entry_photo_url" :src="item.item_entry_photo_url" class="card-photo-thumb" @click.stop="openPhoto(item)" />
            <span class="card-client">{{ item.cliente_nome }}</span>
            <span class="card-service" v-if="item.so_service_name"> — {{ item.so_service_name }}</span>
            <i v-if="item.so_work_order_step_was_resumed === 1" class="pi pi-history card-rescheduled-icon" :title="t('Reagendado')"></i>
            <i class="pi pi-whatsapp card-whatsapp-btn" @click.stop="sendWhatsAppReschedule(item)"></i>
          </div>
          <template v-if="expandedDays.has(cell.dateStr)">
            <div v-for="item in cell.items.slice(MAX_CARDS_PER_DAY)" :key="item.so_work_order_step_id ?? `item-${item.so_order_item_id}`" class="day-card" :class="urgencyDaysClass(item)" :data-testid="`agenda-card-${item.so_order_item_id}`" draggable="true" @dragstart="onDragStart($event, item)" @dragend="onDragEnd" @click.stop="openItemDetail(item)">
              <img v-if="item.item_entry_photo_url" :src="item.item_entry_photo_url" class="card-photo-thumb" @click.stop="openPhoto(item)" />
              <span class="card-client">{{ item.cliente_nome }}</span>
              <span class="card-service" v-if="item.so_service_name"> — {{ item.so_service_name }}</span>
              <i v-if="item.so_work_order_step_was_resumed === 1" class="pi pi-history card-rescheduled-icon" :title="t('Reagendado')"></i>
              <i class="pi pi-whatsapp card-whatsapp-btn" @click.stop="sendWhatsAppReschedule(item)"></i>
            </div>
          </template>
          <span v-if="!expandedDays.has(cell.dateStr) && cell.items.length > MAX_CARDS_PER_DAY" class="more-link" data-testid="agenda-more-items" @click.stop="expandedDays.add(cell.dateStr)">+{{ cell.items.length - MAX_CARDS_PER_DAY }} {{ t('mais') }}</span>
          <span v-if="expandedDays.has(cell.dateStr) && cell.items.length > MAX_CARDS_PER_DAY" class="more-link" data-testid="agenda-show-less" @click.stop="expandedDays.delete(cell.dateStr)">{{ t('Mostrar menos') }}</span>
        </div>
      </div>
    </div>

    <div class="agenda-loading" v-if="loading">
      <i class="pi pi-spin pi-spinner"></i> {{ t('Carregando...') }}
    </div>

    <div v-if="photoModalUrl" class="photo-modal-overlay" @click="closePhoto">
      <div class="photo-modal-content" @click.stop>
        <button class="photo-modal-close" @click="closePhoto" data-testid="photo-modal-close">&times;</button>
        <img :src="photoModalUrl" class="photo-modal-img" />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onBeforeUnmount } from 'vue';
import { useI18n } from 'vue-i18n';
import { useConfirmAction } from '../../composables/useConfirmAction';
import { log } from '../../utils/DebugLogger';
import { useMonthlyAgenda, buildMonthCells } from '../../composables/useMonthlyAgenda';
import { useSODeliveryDate } from '../../composables/useSODeliveryDate';
import { useDateFormatter } from '../../composables/useDateFormatter';
import { eventBus } from '../../eventBus';
import { permissionsState } from '../../services/permissionsService';
import type { Orders_AgendaMensalItemRow } from '../../contracts/Orders_AgendaMensalItemRow';
import type { MonthCell, AgendaMensalDay } from '../../composables/useMonthlyAgenda';

const { t } = useI18n();
const { getAgendaMensal, urgencyDaysClass } = useMonthlyAgenda();
const { updateDeliveryDate } = useSODeliveryDate();
const { toApiDateTimeFormat } = useDateFormatter();
const { confirmAction } = useConfirmAction();

const currentYear = ref(new Date().getFullYear());
const currentMonth = ref(new Date().getMonth() + 1);
const loading = ref(true);
const lastUpdated = ref('');
const currentTime = ref('');

const MAX_CARDS_PER_DAY = 3;
const REFRESH_INTERVAL_MS = 60000;
const expandedDays = ref(new Set<string>());

// Use the typed days shape from Orders_AgendaMensalResponseDados directly —
// no local DayData alias or `as` cast needed.
const daysData = ref<AgendaMensalDay[]>([]);

let refreshTimer: number | null = null;
let clockTimer: number | null = null;

// Day-of-week labels via Intl.DateTimeFormat so they always match the
// user's active locale (pt-BR → "dom seg ter ...", es-CL → "dom lun mar ...").
// Using t('Dom') etc. would feed abbreviated Portuguese words to the translator,
// which Google Translate misinterprets as unrelated words ("Seg" → "Sí mismo").
const dayOfWeekLabels = computed(() => {
  const locale = (permissionsState as { normalizedLocale?: string })?.normalizedLocale || 'pt-BR';
  const formatter = new Intl.DateTimeFormat(locale, { weekday: 'short' });
  // Thursday 1970-01-01 = day index 4 — start from Sunday (index 0) = 1970-01-04
  const base = new Date(1970, 0, 4);
  return Array.from({ length: 7 }, (_, i) => {
    const label = formatter.format(new Date(base.getTime() + i * 86400000));
    // Capitalise first letter, remove trailing dot some locales add (e.g. "dom.")
    return label.charAt(0).toUpperCase() + label.slice(1).replace(/\.$/, '');
  });
});
const monthLabel = computed(() => { const m = [t('Janeiro'), t('Fevereiro'), t('Março'), t('Abril'), t('Maio'), t('Junho'), t('Julho'), t('Agosto'), t('Setembro'), t('Outubro'), t('Novembro'), t('Dezembro')]; return m[currentMonth.value - 1] + ' ' + currentYear.value; });

const monthCells = computed<MonthCell[]>(() => {
  const byDay: Record<number, Orders_AgendaMensalItemRow[]> = {};
  for (const d of daysData.value) { byDay[d.day] = d.items; }
  return buildMonthCells(currentYear.value, currentMonth.value, byDay);
});

const draggedItem = ref<Orders_AgendaMensalItemRow | null>(null);

function openItemDetail(item: Orders_AgendaMensalItemRow): void { eventBus.emit('modal.so-order-item.open', { itemId: item.so_order_item_id }); }

/**
 * Opens a WhatsApp deep-link to notify the client of a rescheduled delivery.
 * window.open is intentional here: this is an external wa.me deep-link;
 * there is no httpService equivalent for opening a third-party URL.
 */
function sendWhatsAppReschedule(item: Orders_AgendaMensalItemRow): void {
  const nome = (item.cliente_nome || '').split(' ')[0];
  const dt = item.so_order_item_expected_delivery_at
    ? new Date(item.so_order_item_expected_delivery_at).toLocaleDateString('pt-BR')
    : t('nova data');
  const msg = t('Olá {nome}! Sua OS foi reagendada para {data}.', { nome, data: dt });
  // api.whatsapp.com/send direto — o redirect do wa.me corrompe emojis no ?text.
  window.open('https://api.whatsapp.com/send/?text=' + encodeURIComponent(msg), '_blank');
}

function openPhoto(item: Orders_AgendaMensalItemRow): void {
  if (item.item_entry_photo_url) photoModalUrl.value = item.item_entry_photo_url;
}
function closePhoto(): void { photoModalUrl.value = null; }
const photoModalUrl = ref<string | null>(null);

function onDragStart(event: DragEvent, item: Orders_AgendaMensalItemRow): void { if (!event.dataTransfer) return; draggedItem.value = item; event.dataTransfer.effectAllowed = 'move'; event.dataTransfer.setData('text/plain', String(item.so_order_item_id)); }
function onDragEnd(): void { draggedItem.value = null; }
function onDragOver(event: DragEvent): void { event.preventDefault(); if (event.dataTransfer) event.dataTransfer.dropEffect = 'move'; (event.currentTarget as HTMLElement)?.classList.add('drag-over'); }
function onDragLeave(event: DragEvent): void { (event.currentTarget as HTMLElement)?.classList.remove('drag-over'); }

async function onDrop(event: DragEvent, cell: MonthCell): Promise<void> {
  event.preventDefault(); (event.currentTarget as HTMLElement)?.classList.remove('drag-over');
  if (!draggedItem.value || !cell.isCurrentMonth) return;
  const newDateStr = cell.dateStr, item = draggedItem.value; draggedItem.value = null;
  if ((item.so_order_item_expected_delivery_at || '').split('T')[0] === newDateStr) return;
  const dd = new Date(newDateStr + 'T12:00:00Z');
  const nf = dd.toLocaleDateString('pt-BR');
  const label = (item.cliente_nome || '--') + ' — ' + (item.so_service_name || '--');
  confirmAction({
    message: 'Reagendar "' + label + '" para ' + nf + '?',
    header: t('Confirmar reagendamento'),
    icon: 'pi pi-calendar',
    rejectLabel: t('Cancelar'),
    acceptLabel: t('Reagendar'),
    onAccept: async () => {
      try {
        await updateDeliveryDate(item.so_order_item_id, {
          expectedDeliveryAt: toApiDateTimeFormat(dd),
          reason: t('Reagendado via Agenda de produção'),
        });
        await fetchData();
        eventBus.emit('globalMessage.success', t('Data de entrega atualizada'));
      } catch (err) {
        log('PDVAgendaMensalPage', 'onDrop', 'Error', err);
      }
    },
  });
}

function prevMonth(): void { if (currentMonth.value === 1) { currentMonth.value = 12; currentYear.value--; } else { currentMonth.value--; } fetchData(); }
function nextMonth(): void { if (currentMonth.value === 12) { currentMonth.value = 1; currentYear.value++; } else { currentMonth.value++; } fetchData(); }

const updateClock = (): void => { currentTime.value = new Date().toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit', second: '2-digit' }); };

const fetchData = async (): Promise<void> => {
  try {
    const dados = await getAgendaMensal(currentYear.value, currentMonth.value);
    daysData.value = dados ? dados.days : [];
    lastUpdated.value = new Date().toLocaleTimeString('pt-BR');
  } catch (err) {
    log('PDVAgendaMensalPage', 'fetchData', 'Error', err);
  } finally {
    loading.value = false;
  }
};

onMounted(() => { fetchData(); updateClock(); refreshTimer = window.setInterval(fetchData, REFRESH_INTERVAL_MS); clockTimer = window.setInterval(updateClock, 1000); });
onBeforeUnmount(() => { if (refreshTimer) clearInterval(refreshTimer); if (clockTimer) clearInterval(clockTimer); });
</script>

<style scoped>
.agenda-mensal-page { padding: 20px; min-height: calc(100vh - 60px); background: var(--p-surface-50, #fafafa); }
.agenda-header { display: flex; justify-content: space-between; align-items: center; gap: 12px; margin-bottom: 12px; padding: 14px 18px; background: var(--p-surface-0, #fff); border: 1px solid var(--p-surface-200, #e5e7eb); border-radius: 12px; }
.agenda-header-left { display: flex; align-items: center; gap: 10px; }
.agenda-header-icon { font-size: 1.3rem; color: var(--p-primary-color); }
.agenda-header h1 { margin: 0; font-size: 1.2rem; color: var(--p-text-color, #333); }
.agenda-controls { display: flex; align-items: center; gap: 12px; }
.agenda-nav-btn { background: var(--p-surface-0, #fff); border: 1px solid var(--p-surface-200, #e5e7eb); border-radius: 8px; padding: 6px 10px; cursor: pointer; color: var(--p-primary-color); font-size: 0.9rem; transition: background 0.2s; }
.agenda-nav-btn:hover { background: var(--p-surface-100, #f3f4f6); }
.agenda-month-label { font-size: 1.1rem; font-weight: 600; min-width: 150px; text-align: center; }
.agenda-refresh-icon { font-size: 1rem; color: var(--p-primary-color); cursor: pointer; transition: transform 0.2s; }
.agenda-refresh-icon:hover { transform: rotate(90deg); }
.agenda-updated { font-size: 0.8rem; color: var(--p-text-muted-color, #888); }
.agenda-clock { font-size: 1.1rem; font-weight: 600; color: var(--p-primary-color); font-family: 'Courier New', monospace; background: var(--p-surface-100, #f3f4f6); padding: 3px 10px; border-radius: 5px; }
.agenda-legend { display: flex; align-items: center; justify-content: center; gap: 14px; margin-bottom: 10px; padding: 6px 14px; background: var(--p-surface-0, #fff); border: 1px solid var(--p-surface-200, #e5e7eb); border-radius: 8px; font-size: 0.75rem; }
.legend-item { display: inline-flex; align-items: center; gap: 4px; color: var(--p-text-color, #555); }
.legend-swatch { display: inline-block; width: 10px; height: 10px; border-radius: 2px; border: 1px solid rgba(0,0,0,0.1); }
.legend-overdue { background: var(--p-red-500, #ef4444); }
.legend-today { background: var(--p-orange-500, #f97316); }
.legend-tomorrow { background: var(--p-blue-500, #3b82f6); }
.legend-upcoming { background: var(--p-green-500, #22c55e); }
.legend-rescheduled { background: var(--p-gray-400, #9ca3af); }
.agenda-dow-header { display: grid; grid-template-columns: repeat(7, 1fr); gap: 4px; margin-bottom: 4px; }
.agenda-dow-cell { text-align: center; font-weight: 600; font-size: 0.8rem; color: var(--p-text-muted-color, #888); padding: 4px 0; }
.agenda-grid { display: grid; grid-template-columns: repeat(7, 1fr); gap: 4px; }
.agenda-day-cell { min-height: 110px; border: 1px solid var(--p-surface-200, #e5e7eb); border-radius: 8px; padding: 6px; background: var(--p-surface-0, #fff); display: flex; flex-direction: column; transition: box-shadow 0.15s; overflow: hidden; }
.agenda-day-cell:hover { box-shadow: 0 1px 4px rgba(0,0,0,0.08); }
.agenda-day-cell.is-today { border-color: var(--p-primary-color); background: var(--p-primary-50, #eff6ff); }
.agenda-day-cell.is-other-month { background: var(--p-surface-50, #fafafa); opacity: 0.5; }
.agenda-day-cell.is-other-month .day-number { color: var(--p-surface-400, #9ca3af); }
.agenda-day-cell.has-items { cursor: pointer; }
.day-number { font-size: 0.8rem; font-weight: 600; color: var(--p-text-color, #333); margin-bottom: 4px; display: block; }
.is-today .day-number { color: var(--p-primary-color); font-weight: 700; }
.day-cards { display: flex; flex-direction: column; gap: 2px; flex: 1; overflow: hidden; }
.day-card { font-size: 0.6rem; line-height: 1.2; padding: 2px 4px; border-left: 3px solid transparent; border-radius: 4px; background: var(--p-surface-50, #f9fafb); transition: opacity 0.15s; display: flex; align-items: center; gap: 4px; min-width: 0; }
.day-card .card-client { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; min-width: 0; flex-shrink: 1; }
.day-card .card-service { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; min-width: 0; flex-shrink: 1; }
.day-card[draggable="true"] { cursor: grab; }
.day-card[draggable="true"]:active { cursor: grabbing; opacity: 0.5; }
.day-card.urgency-overdue { border-left-color: var(--p-red-500, #ef4444); background: var(--p-red-50, #fef2f2); }
.day-card.urgency-today { border-left-color: var(--p-orange-500, #f97316); background: var(--p-orange-50, #fff7ed); }
.day-card.urgency-tomorrow { border-left-color: var(--p-blue-500, #3b82f6); background: var(--p-blue-50, #eff6ff); }
.day-card.urgency-upcoming { border-left-color: var(--p-green-500, #22c55e); background: var(--p-green-50, #f0fdf4); }
.day-card.urgency-rescheduled { border-left-color: var(--p-gray-400, #9ca3af); background: var(--p-gray-50, #f9fafb); }
.card-photo-thumb { width: 16px; height: 16px; border-radius: 4px; flex-shrink: 0; cursor: pointer; overflow: hidden; border: 1px solid var(--p-surface-200, #e5e7eb); }
.card-whatsapp-btn { font-size: 0.7rem; color: #25D366; cursor: pointer; flex-shrink: 0; margin-left: auto; transition: transform 0.15s; }
.card-whatsapp-btn:hover { transform: scale(1.2); }
.card-rescheduled-icon { font-size: 0.7rem; color: #9ca3af; cursor: default; flex-shrink: 0; margin-left: 4px; }
.agenda-day-cell.drag-over { background: var(--p-primary-50, #eff6ff); border-color: var(--p-primary-color, #3b82f6); box-shadow: 0 0 0 2px var(--p-primary-300, #93c5fd); }
.more-link { font-size: 0.6rem; color: var(--p-primary-color); font-weight: 600; cursor: pointer; padding: 1px 4px; }
.agenda-loading { display: flex; align-items: center; justify-content: center; gap: 8px; padding: 60px; color: var(--p-text-muted-color, #888); font-size: 1rem; }
.photo-modal-overlay { position: fixed; top: 0; left: 0; right: 0; bottom: 0; background: rgba(0,0,0,0.7); display: flex; align-items: center; justify-content: center; z-index: 9999; }
.photo-modal-content { position: relative; max-width: 90vw; max-height: 90vh; }
.photo-modal-close { position: absolute; top: -30px; right: 0; background: none; border: none; color: #fff; font-size: 28px; cursor: pointer; }
.photo-modal-img { max-width: 90vw; max-height: 85vh; border-radius: 8px; object-fit: contain; }
</style>
